import type { Action } from "redux";
import { createLogic } from "redux-logic";
import * as DOMUtils from "history/DOMUtils";
import HistoryLogic from "./basket/HistoryLogic";
import type {
    AddExtraType,
    AddPackExtraType,
    AddPackType,
    AddServiceProductType,
    CalculatePriceType,
    RemoveExtraType,
    RemoveServiceProductType,
    ResolveExtraConflictType,
    ResolvePackConflictType,
    ResolveServiceProductConflictType,
    SetColourType,
    SetExtraOptionType,
    SetPrimaryAccentColourType,
    SetSecondaryAccentColourType,
    SetWheelType,
} from "../actions/BasketActions";
import {
    ADD_EXTRA,
    ADD_PACK,
    ADD_PACK_EXTRA,
    ADD_SERVICE_PRODUCT,
    addExtra,
    addPack,
    addPackExtra,
    addServiceProduct,
    CALCULATE_PRICE,
    REMOVE_EXTRA,
    REMOVE_PACK,
    REMOVE_PACK_EXTRA,
    REMOVE_SERVICE_PRODUCT,
    removeExtra,
    removePack,
    removePackExtra,
    removeServiceProduct,
    RESOLVE_CONFLICT_PACK,
    RESOLVE_EXTRA_CONFLICTS,
    RESOLVE_SERVICE_PRODUCT_CONFLICT,
    SET_COLOUR,
    SET_EXTRA_OPTION,
    SET_PRIMARY_ACCENT_COLOUR,
    SET_SECONDARY_ACCENT_COLOUR,
    SET_UPHOLSTERY,
    SET_WHEEL,
    setPrimaryAccentColour,
    setSecondaryAccentColour,
    setValidatedConfiguration,
    setWheel,
    updateExtraSetItems,
} from "../actions/BasketActions";
import CarDataConsolidator from "../../../../shared-logic/utils/classes/CarDataConsolidator";
import { checkDiff, find } from "../../../../../common-deprecated/utils";
import { resetFinanceRates, startFinanceRateFetch, startInsuranceRateFetch } from "../actions/FinanceInsuranceActions";
import Debug from "../../../../../common-deprecated/Debug";
import { firstUserSelectedLogic, lastUserSelectedLogic } from "./basket/UserSelectedLogic";
import externalPriceLogic from "./basket/ExternalPriceLogic";
import { setExternalPriceError, startExternalPriceValueFetch } from "../actions/ExternalPriceActions";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import { getValidatedConfig, ValidateConfigAction } from "../../../../shared-logic/utils/ValidateConfig";
import { showModal } from "../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_EXTRA_CONFLICT, MODAL_PACK_CONFLICT, MODAL_SERVICE_PRODUCT_CONFLICT } from "../../utils/modalConstants";
import { getDefaultPriceObject } from "../../../../../common-deprecated/constants";
import { startConfigSave } from "../actions/SaveActions";
import {
    getPersonalFinanceInsuranceEnabled,
    showBnbInsurance,
    showBnbMonthlyRates,
} from "../../utils/settings/bnbFinanceSettingUtils";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { setFilterEquipment } from "../../../../../common-deprecated/redux/actions/UtilActions";
import { showTradeIn } from "../../utils/settings/bnbSettingUtils";
import type { BuildAndBuyDispatchType, BuildAndBuyLogicType, BuildAndBuyStateType } from "../store";
import type { BasketType } from "../../../../shared-logic/types/BasketType";
import type {
    AccentColourType,
    ExtraType,
    PackType,
    WheelType,
} from "../../../../../common-deprecated/types/CommonTypes";
import { addNotification } from "../../../../../common-deprecated/redux/actions/NotificationActions";
import { getBnbLabel } from "../../utils/labels";
import { calculateTotalPrice } from "../../../../shared-logic/utils/priceUtils";
import { getTradeInCashNotificationConfig } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtils";
import { setTradeInNotification } from "../../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { findPackExtraParent } from "../../../../shared-logic/utils/typedUtils";
import { getAEMLicenseFee } from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { isB2bPossible } from "../../utils/selectorUtils";
import { hasAlternativeEcoSpecFlow } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { startEcoConsumptionCostsFetch } from "../../../../shared-modules/eco-consumption-section/redux/reducers/EcoConsumptionReducer";
import { getConfigSpecsBody } from "../../utils/techSpecUtils";
import { PriceCalculationServiceEndpoint } from "../../../../../common-deprecated/types/SpecTypes";
import { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";

const calculatePriceLogic = (createLogic as BuildAndBuyLogicType<CalculatePriceType>)({
    // Calculate price is always called even for NMSC's which don't have prices (EU/EN, ZZ/EN)
    type: CALCULATE_PRICE,

    validate({ getState, action }, allow, reject) {
        const { basket, car, externalPrice, navigation, commonSettings, bnbSettings } = getState();

        // If externalPrice is enabled but still fetching the functions, do not calculate.
        // When done externalPrice logic will re-trigger calculate.
        if (externalPrice.enabled && externalPrice.fetchingFunctions && !externalPrice.error)
            return (reject as () => void)();

        // Calculate the total price
        const calculatedTotal = calculateTotalPrice(
            getDefaultPriceObject(),
            basket,
            car,
            showBnbMonthlyRates(commonSettings, bnbSettings, car.model.internalCode, navigation.step, car),
        ) as BasketType["total"];

        // Save previous total to determine if the monthly rate needs to be reloaded
        action.previousTotal = basket.total;

        // If externalPrice.error is true this means the externalPrice price has been calculated for the current configuration but failed.
        // In that case don't use externalPrice pricing even though externalPrice is enabled.
        if (externalPrice.enabled && externalPrice.ready && !externalPrice.error) {
            // Check should change to see if something in the configuration changed
            // Always reload the external price, even if the price differs. There are scenario's when adding for instance a pack without price, it pushes
            // the taxes into a new tier (due to more emissions), if so the co2 value will update and so will the taxes on all items.
            if (action.loadExternalPrice) {
                // externalPrice originalTotalPrice contains the last price where externalPrice rates were fetched for.
                // Trigger a new externalPrice price fetch, the fetch will trigger a new calculate action when done.
                // Loading external price also encapsulates loading mass management data
                return reject(startExternalPriceValueFetch(calculatedTotal, action.firstTime));
            }
            const { totalValue } = externalPrice.calculatedPrice;
            calculatedTotal.cash = totalValue;
            calculatedTotal.discount = totalValue;
            calculatedTotal.onlineCashDiscount = totalValue;
            calculatedTotal.onlineMonthlyDiscount = totalValue;
            calculatedTotal.discountTax = totalValue - externalPrice.originalTotalPrice.discount;
            calculatedTotal.discountWithoutTax = externalPrice.originalTotalPrice.discount;
            calculatedTotal.isExternalPrice = true;
        }

        // Fill in the license fee if applicable
        calculatedTotal.licenseFee = getAEMLicenseFee(
            commonSettings,
            car.price.cash,
            car.model.internalCode,
            AemFmComponent.Build,
        );

        action.total = calculatedTotal;

        return allow(action);
    },

    process({ getState, action }, dispatch, done) {
        const state = getState();
        const { commonSettings, bnbSettings, car, navigation, externalPrice, finance, specs, utils } = state;
        const { tradeInAemExternalData } = getState();
        const isB2B = isB2bPossible(state);

        // Reset externalPrice error state for current price calculation.
        if (externalPrice.enabled && externalPrice.ready && externalPrice.error) dispatch(setExternalPriceError(false));

        if (DOMUtils.canUseDOM) {
            const showFinanceRates = showBnbMonthlyRates(
                commonSettings,
                bnbSettings,
                car.model.internalCode,
                navigation.step,
                car,
            );
            const showInsuranceRates = showBnbInsurance(commonSettings, bnbSettings, car);
            const { personalFinanceEnabled } = getPersonalFinanceInsuranceEnabled(bnbSettings);
            const financeQuotes = finance.quotes;
            const { enableFinanceCompare } = bnbSettings;
            if (showFinanceRates || showInsuranceRates) {
                // If externalPrice is active, this happens after externalPrice price is fetched.
                // Don't update the finance rate if externalPrices are active, but an error occurred!
                if (externalPrice.enabled && externalPrice.hadError) {
                    dispatch(resetFinanceRates());
                } else {
                    if (action.firstTime && navigation.step === NavStep.Summary) {
                        dispatch(startConfigSave());
                    }
                    if (
                        showFinanceRates &&
                        !isB2B &&
                        (!enableFinanceCompare ||
                            (enableFinanceCompare && (!personalFinanceEnabled || financeQuotes.finance)))
                    ) {
                        dispatch(
                            // Aligned with the startFinanceRateFetch in FinanceInsuranceLogic -> updateQuoteConfig
                            startFinanceRateFetch(
                                action.firstTime ? FinanceUpdateTrigger.NewQuote : FinanceUpdateTrigger.NewConfig,
                            ),
                        );
                    }
                    if (showInsuranceRates) dispatch(startInsuranceRateFetch());
                }

                // Trade-in finance integration: Check if a notification should be shown.
                if (
                    showTradeIn(bnbSettings, commonSettings, utils.financeOption, finance.info.productName) &&
                    tradeInAemExternalData.data.vehicleValue
                ) {
                    const { notificationType, isError, show } = getTradeInCashNotificationConfig(
                        tradeInAemExternalData.data.vehicleValue || 0,
                        action.total?.cash || 0,
                        tradeInAemExternalData.notification.cash.notificationType,
                        tradeInAemExternalData.notification.cash.show,
                    );

                    // OR-1356 - Do not show popup when retrieving an error message.
                    const actualShow = isError ? false : show;
                    dispatch(setTradeInNotification("cash", actualShow, notificationType, isError));
                }
            }
            if (hasAlternativeEcoSpecFlow(commonSettings)) {
                dispatch(
                    startEcoConsumptionCostsFetch({
                        ecoConsumptionCostService: bnbSettings.ecoConsumptionCostService,
                        commonSettings,
                        // We pass an override to ensure Prices method is used later on because eco consumption cost calculation depends on it
                        config: getConfigSpecsBody(getState(), true, PriceCalculationServiceEndpoint.Prices),
                        techSpecs: specs.techSpecs,
                        engine: car.motorization.engine,
                    }),
                );
            }
        }
        done();
    },
});

/**
 * Method which resolves the logic when an extra's setItems are updated due to colour dependencies
 * @param getState
 * @param dispatch
 * @param transactionId
 */
export const resolveSetItemColourDependencies = (
    getState: () => BuildAndBuyStateType,
    dispatch: BuildAndBuyDispatchType,
    transaction: boolean | string,
): void => {
    const { items, basket } = getState();
    const { extras, colour, upholstery, primaryAccentColour, secondaryAccentColour } = basket;

    // Get all the selected extras with sets
    extras.forEach((extra) => {
        const itemExtra = find(extra.id, items.extras);
        // Check if the setItems of each extra still validate for the current selected colour & upholstery
        if (itemExtra && itemExtra.setItems.length) {
            const colourDependendSetItems = itemExtra.setItems.filter(
                (setItem) =>
                    // Only show extras which are available for the current colour & upholstery combination.
                    CarDataConsolidator.isAvailable(setItem, colour, upholstery).available &&
                    // Also check the colouringmode
                    CarDataConsolidator.validatesForColourMode(setItem as ExtraType, {
                        colour,
                        primaryAccentColour,
                        secondaryAccentColour,
                    }),
            );
            // Check both the difference in the basket.extras[x]
            if (checkDiff(extra.setItems, colourDependendSetItems)) {
                dispatch(updateExtraSetItems(itemExtra, colourDependendSetItems, transaction));
            }
        }
    });
};

const setColourLogic = (createLogic as BuildAndBuyLogicType<SetColourType>)({
    type: SET_COLOUR,

    process({ getState, action }, dispatch, done) {
        const { basket, items } = getState();
        const validatedConfig = getValidatedConfig(basket, ValidateConfigAction.Colour, items, action.colour.id);
        dispatch(setValidatedConfiguration(validatedConfig));
        resolveSetItemColourDependencies(getState, dispatch, action.transaction);
        done();
    },
});

// TODO: what to do with this? Make upholsteries into ExtraType? very confusing
const setUpholsteryLogic = (createLogic as BuildAndBuyLogicType<any>)({
    type: SET_UPHOLSTERY,

    validate({ getState, action }, allow, reject) {
        const { upholstery } = action;
        if (upholstery.isUpholstery) {
            // Check if it is a pack upholstery
            const { basket } = getState();
            const basketPackUpholsteries = CarDataConsolidator.getBasketPackUpholsteries(
                basket.packs,
                true,
                basket.colour.id,
            );
            if (basketPackUpholsteries.length && find(upholstery.id, basketPackUpholsteries)) {
                // EquipmentUpholstery part of a pack
                const parentPack = findPackExtraParent(upholstery.id, basket.packs);
                reject(addPackExtra(parentPack, upholstery, [], false, action.transaction, action));
            } else {
                // EquipmentUpholstery, which is an extra so use addExtra instead.
                // ValidateConfig will take care of the applicable upholstery logic.
                reject(addExtra(upholstery, false, action.transaction));
            }
        } else {
            allow(action);
        }
    },

    process({ getState, action }, dispatch, done) {
        const { basket, items } = getState();
        const validatedConfig = getValidatedConfig(
            basket,
            ValidateConfigAction.Upholstery,
            items,
            action.upholstery.id,
        );
        dispatch(setValidatedConfiguration(validatedConfig));
        resolveSetItemColourDependencies(getState, dispatch, action.transaction);
        done();
    },
});

/**
 * Method which resolves the include exclude rules of adding an extra
 * @param getState
 * @param extra
 * @param dispatch
 */
export const resolveAddExtraIncludExcludeRules = (
    getState: () => BuildAndBuyStateType,
    extra: ExtraType,
    dispatch: BuildAndBuyDispatchType,
    transactionId: string | boolean,
    extraPendingStack?: ExtraType[],
): void => {
    const { items, basket, transaction } = getState();
    const childOption = CarDataConsolidator.getSelectedExtraOption(extra);

    // Remove extras excluded by the selected extra, its child-option & its set-items
    CarDataConsolidator.getExcludedExtras(extra, basket.extras, childOption).forEach((selectedExtra) =>
        dispatch(removeExtra(selectedExtra, false, transactionId)),
    );

    // Add non conflicting included extras.
    CarDataConsolidator.getNonConflictingIncludedExtras(extra, items.extras, basket).forEach((includedExtra) =>
        dispatch(addExtra(includedExtra, true, transactionId)),
    );

    // If the remove action which triggered this logic is part of a wheel transaction do not take care of the wheel include logic updates as well.
    const isWheelTransaction =
        typeof transactionId === "boolean"
            ? false
            : transaction.active[transactionId] && transaction.active[transactionId].action === SET_WHEEL;

    // Add the next extra when resolving a conflict.
    if (extraPendingStack && extraPendingStack.length > 0) {
        const remainingStack = extraPendingStack.slice(1);
        if (extraPendingStack[0].isWheel)
            dispatch(setWheel(extraPendingStack[0] as unknown as WheelType, true, transactionId, remainingStack));
        else dispatch(addExtra(extraPendingStack[0], extraPendingStack.length > 1, transactionId, "", remainingStack));
    }

    // Extra -> include/exclude a wheel
    if (!extra.isWheel && !isWheelTransaction) {
        // If the (child)extra includes a wheel, select it.
        const includedWheel = items.wheels.find((wheel) => {
            return childOption
                ? extra.included.concat(childOption.included).find((includedExtra) => wheel.id === includedExtra)
                : extra.included.find((includedExtra) => wheel.id === includedExtra);
        });
        if (includedWheel && includedWheel.id !== basket.wheel.id) {
            dispatch(setWheel(includedWheel, false, transactionId));
        }
        // If the extra excludes a wheel(s), make sure the default first available wheel is selected
        const excludedWheels = items.wheels.filter((wheel) => {
            return childOption
                ? extra.excluded.concat(childOption.excluded).find((excludedExtra) => wheel.id === excludedExtra)
                : extra.excluded.find((excludedExtra) => wheel.id === excludedExtra);
        });
        if (excludedWheels.length && find(basket.wheel.id, excludedWheels)) {
            const firstAvailableWheel = items.wheels.filter((wheel) => !find(wheel.id, excludedWheels))[0];
            if (firstAvailableWheel) {
                dispatch(setWheel(firstAvailableWheel, false, transactionId));
            } else Debug.error(`No wheel found for selection due to exclude rules defined in: ${extra.name}`);
        }
    }
};

const addExtraLogic = (createLogic as BuildAndBuyLogicType<AddExtraType>)({
    type: ADD_EXTRA,

    validate({ getState, action }, allow, reject) {
        const { items, basket } = getState();
        const { extras } = basket;

        // Equipment is already added, do nothing.
        if (find(action.extra.id, extras)) {
            return (reject as () => void)();
        }

        const conflicts = CarDataConsolidator.extraHasConflicts(action.extra, items.extras, extras);
        const parentPack = basket.packs.length ? findPackExtraParent(action.extra.id, basket.packs) : null;
        const parentPackEquipment = parentPack ? find(action.extra.id, parentPack.equipment) : false;
        if (parentPack && parentPackEquipment) {
            // Add an extra as packExtra when the pack is already selected
            allow(addPackExtra(parentPack, parentPackEquipment as ExtraType, [], false, true, false));
        } else if (!conflicts) {
            if (!action.optionId && action.extra.options.length) action.optionId = action.extra.options[0].id;
            allow(action);
        } else {
            reject(
                showModal({
                    type: MODAL_EXTRA_CONFLICT,
                    extra: action.extra,
                    conflicts,
                    extraPendingStack: action.extraPendingStack || [],
                }),
            );
        }
    },

    process({ getState, action }, dispatch, done) {
        const { utils } = getState();

        const equipmentId = utils.filterEquipment.split(":")[1];
        if (action.extra.id === equipmentId) dispatch(setFilterEquipment(`true:${equipmentId}`));

        resolveAddExtraIncludExcludeRules(
            getState,
            action.extra,
            dispatch,
            action.transaction,
            action.extraPendingStack,
        );
        if (action.extra.isUpholstery || action.extra.includePacks.length || action.extra.excludePacks.length) {
            const { basket, items } = getState();
            const validatedConfig = getValidatedConfig(basket, ValidateConfigAction.AddExtra, items, action.extra.id);
            dispatch(setValidatedConfiguration(validatedConfig));
        }
        resolveSetItemColourDependencies(getState, dispatch, action.transaction);
        done();
    },
});

/**
 * Method which resolves the include exclude rules of removing an extra
 * @param getState
 * @param removedExtra
 * @param dispatch
 */
export const resolveRemoveExtraIncludeExcludeRules = (
    getState: () => BuildAndBuyStateType,
    removedExtra: ExtraType,
    dispatch: BuildAndBuyDispatchType,
    transactionId: string | boolean,
    extraPendingStack?: ExtraType[],
): void => {
    const { items, transaction } = getState();
    let { basket } = getState();
    const childOption = CarDataConsolidator.getSelectedExtraOption(removedExtra);

    // Returns a list of extras which have includes towards the provided extra
    const includedBy = CarDataConsolidator.getExtrasIncludedBy(removedExtra, basket.extras, childOption);

    // Get the extras in the basket included by this extra.
    const includes = CarDataConsolidator.getIncludedExtras(removedExtra, basket.extras)
        // Filter the user selected extras
        .filter((includedExtra) => !includedExtra.userSelected)
        // filter extras included by other selected extras or wheel.
        .filter(
            (includedExtra) =>
                !basket.extras
                    .filter((selectedExtra) => selectedExtra.id !== removedExtra.id)
                    .find((selectedExtra) => selectedExtra.included.includes(includedExtra.id)) &&
                !basket.wheel.included.includes(includedExtra.id),
        );

    includedBy.concat(includes).forEach((includedExtra) => dispatch(removeExtra(includedExtra, false, transactionId)));

    // If the currently removed item is not a wheel, we have to take care of possible wheel includes.
    // If the removed extra is part of the wheel currently in the basket, the wheel should be deselected.
    // However, There are some edge cases where we don't want to execute this wheel include logic:

    // If a wheel is still pending (=> A wheel with extra includes is in the process of being selected and this
    // extra is part its include/exclude rule logic), we don't have to check wheel include logic for this extra.
    // This could possibly result in the original pending wheel being overwritten.
    const wheelIsPending = extraPendingStack && extraPendingStack[0] && extraPendingStack[0].isWheel;

    // If the remove action which triggered this logic is part of a wheel transaction do not take care of the wheel include logic updates as well.
    // The same logic applies as described above, this could result in the original wheel being overwritten.
    const isWheelTransaction =
        typeof transactionId === "boolean"
            ? false
            : transaction.active[transactionId] && transaction.active[transactionId].action === SET_WHEEL;

    if (!removedExtra.isWheel && !isWheelTransaction && !wheelIsPending) {
        // Important: Refresh basket state as it is possible that the wheel in the basket is already updated because of
        // the include/exclude rules of the removeExtra dispatch earlier in this function.
        basket = getState().basket; // eslint-disable-line prefer-destructuring
        const includedByWheel = basket.wheel.included.find((wheelInclude) => {
            return childOption
                ? wheelInclude === removedExtra.id || wheelInclude === childOption.id
                : wheelInclude === removedExtra.id;
        });
        if (includedByWheel) {
            // Get the first wheel which doesn't have this extra included
            const newWheel = items.wheels.find((wheel) => {
                return !wheel.included.find((wheelInclude) => {
                    return childOption
                        ? wheelInclude === removedExtra.id || wheelInclude === childOption.id
                        : wheelInclude === removedExtra.id;
                });
            });
            if (newWheel) dispatch(setWheel(newWheel, false, transactionId));
            else Debug.error(`No wheel found to fall back to which doesn't include: ${removedExtra.name}`);
        }
    }
};

const removeExtraLogic = (createLogic as BuildAndBuyLogicType<RemoveExtraType>)({
    type: REMOVE_EXTRA,

    validate({ getState, action }, allow, reject) {
        const { basket } = getState();

        // Equipment is already added, do nothing.
        if (!find(action.extra.id, basket.extras)) {
            return (reject as () => void)();
        }

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        const { utils } = getState();

        const equipmentId = utils.filterEquipment.split(":")[1];
        if (action.extra.id === equipmentId) dispatch(setFilterEquipment(`false:${equipmentId}`));

        resolveRemoveExtraIncludeExcludeRules(getState, action.extra, dispatch, action.transaction);
        if (action.extra.includePacks.length || action.extra.excludePacks.length) {
            const { basket, items } = getState();
            const validatedConfig = getValidatedConfig(
                basket,
                ValidateConfigAction.RemoveExtra,
                items,
                action.extra.id,
            );
            dispatch(setValidatedConfiguration(validatedConfig));
        }
        done();
    },
});

const resolveExtraConflict = (createLogic as BuildAndBuyLogicType<ResolveExtraConflictType>)({
    type: RESOLVE_EXTRA_CONFLICTS,
    process({ action }, dispatch, done) {
        const extraPendingStack = [action.extra, ...action.extraPendingStack].filter(
            (extra) => extra.id !== action.selectedExtra.id,
        );
        dispatch(addExtra(action.selectedExtra, true, action.transaction, "", extraPendingStack));
        done();
    },
});

const setExtraOptionLogic = (createLogic as BuildAndBuyLogicType<SetExtraOptionType>)({
    type: SET_EXTRA_OPTION,

    validate({ getState, action }, allow, reject) {
        const { basket } = getState();
        const { extra, transaction, optionId } = action;

        const extraSelected = !!find(action.extra.id, basket.extras);
        if (!extraSelected) reject(addExtra(extra, false, transaction, optionId));
        else allow(action);
    },
});

const getRootAction = (action: Action & { parentAction?: Action | false }): Action => {
    if (action.parentAction) {
        return getRootAction(action.parentAction);
    }
    return action;
};

const setPrimaryAccentColourLogic = (createLogic as BuildAndBuyLogicType<SetPrimaryAccentColourType>)({
    type: [SET_PRIMARY_ACCENT_COLOUR],
    validate({ getState, action }, allow, reject) {
        const { basket } = getState();
        if (basket.primaryAccentColour && basket.primaryAccentColour.id === action.colour.id) {
            return (reject as () => void)();
        }
        if (
            action.colour.accentColours.length > 0 &&
            (!basket.secondaryAccentColour || !find(basket.secondaryAccentColour.id, action.colour.accentColours))
        ) {
            return reject(
                setSecondaryAccentColour(
                    action.pack,
                    action.colour.accentColours.find((colour) => colour.default) || action.colour.accentColours[0],
                    action,
                    false,
                    action.transaction,
                ),
            );
        }
        allow(action);
    },
    process({ getState, action }, dispatch, done) {
        const { basket } = getState();
        if (action.pack && !find(action.pack.id, basket.packs)) {
            dispatch(addPack(action.pack, [], [], false, action, false, action.transaction));
            // ensure that the secondary accentColour is set and ensure it exists for the primary one.
        } else if (action.parentAction && !action._snapshot) {
            dispatch({ ...getRootAction(action), parentAction: action } as unknown as SetPrimaryAccentColourType);
        }

        basket.packs
            .filter((pack) => !!pack.extended)
            .forEach((pack) => {
                if (
                    pack.extended?.accentColours[basket.colour.id] &&
                    !find(action.colour.id, pack.extended.accentColours[basket.colour.id])
                ) {
                    dispatch(removePack(pack, action.transaction));
                }
            });
        resolveSetItemColourDependencies(getState, dispatch, action.transaction);
        done();
    },
});

const setSecondaryAccentColourLogic = (createLogic as BuildAndBuyLogicType<SetSecondaryAccentColourType>)({
    type: [SET_SECONDARY_ACCENT_COLOUR],
    process({ getState, action }, dispatch, done) {
        const { basket } = getState();
        if (action.pack && !find(action.pack.id, basket.packs)) {
            dispatch(addPack(action.pack, [], [], false, action, false, action.transaction));
        } else if (action.parentAction && !action._snapshot) {
            dispatch({ ...getRootAction(action), parentAction: action } as unknown as SetSecondaryAccentColourType);
        }
        resolveSetItemColourDependencies(getState, dispatch, action.transaction);
        done();
    },
});

const addPackSelectionLogic = (createLogic as BuildAndBuyLogicType<AddPackType | AddPackExtraType>)({
    type: [ADD_PACK, ADD_PACK_EXTRA],

    validate({ getState, action }, allow, reject) {
        const { basket, items } = getState();
        if (
            (action.type === ADD_PACK && !!find(action.pack.id, basket.packs)) ||
            (action.type === ADD_PACK_EXTRA && !!find(action.extra.id, basket.packExtras))
        ) {
            return (reject as () => void)();
        }

        // Extract action variables.
        const { selectedExtras, selectedPacks, automatic, pack, extra } = action;

        if (
            pack.extended &&
            pack.extended.accentColours[basket.colour.id] &&
            (!basket.primaryAccentColour ||
                !find(basket.primaryAccentColour.id, pack.extended.accentColours[basket.colour.id]))
        ) {
            const primaryAccentColour = pack.extended.accentColours[basket.colour.id].find(
                (accentColour: AccentColourType) => accentColour.default,
            );
            if (primaryAccentColour) {
                return reject(setPrimaryAccentColour(pack, primaryAccentColour, action, undefined, false));
            }
        }

        // Add new equipment to selectedExtras.
        // The action is automatic when it is called by resolve functions and not directly by the user.
        // This means the addedPack is already added to the selectedExtras.
        if (extra && !automatic) {
            selectedExtras.push(extra);
        }

        // For conflict resolution we need to combine the currently selected extras and the basket extras.
        // Get all the selected packExtras excluding the selectedExtras
        const additionalExtras = basket.packExtras.filter((packExtra) => !find(packExtra.id, selectedExtras));
        let combinedSelectedExtras = automatic ? selectedExtras : [...selectedExtras, ...additionalExtras];
        // Remove the excluded equipment from the combined selected extras!
        if (extra) {
            // Array used to determine the standard conflicts, filter out the excludes present in the newly selected pack extra
            combinedSelectedExtras = combinedSelectedExtras.filter(
                (selectedExtra: ExtraType) => extra.excluded.indexOf(selectedExtra.id) === -1,
            );
        }

        // Check for conflicts within standard packs.
        let standardConflicts = CarDataConsolidator.packHasStandardConflicts(
            CarDataConsolidator.getExtrasBasedOnColour(basket, pack.visualItems.standard),
            CarDataConsolidator.getExtrasBasedOnColour(basket, pack.visualItems.optional),
            CarDataConsolidator.getExtrasBasedOnColour(basket, pack.equipment as ExtraType[]),
            combinedSelectedExtras,
        );
        if (standardConflicts) {
            // Sort the standardConflicts based on their index in the pack.equipment for visual consistency in the overlay
            standardConflicts = pack.equipment.filter(
                (packExtra) => !!find(packExtra.id, standardConflicts as ExtraType[]),
            ) as ExtraType[];
            return reject(
                showModal({
                    type: MODAL_PACK_CONFLICT,
                    pack,
                    conflicts: standardConflicts,
                    selectedConflicts: selectedExtras,
                    isConflictBetweenPackExtras: true,
                }),
            );
        }

        // Check for conflicts within optional packs, which can be handled by extra logic.
        if (extra) {
            let equipmentConflicts = CarDataConsolidator.extraHasConflicts(
                extra,
                CarDataConsolidator.getExtrasBasedOnColour(basket, pack.equipment as ExtraType[]),
                combinedSelectedExtras,
            );
            if (equipmentConflicts) {
                // Sort the equipmentConflicts based on their index in the pack.equipment for visual consistency in the overlay
                equipmentConflicts = pack.equipment.filter(
                    (packExtra) => !!find(packExtra.id, equipmentConflicts as ExtraType[]),
                ) as ExtraType[];
                return reject(
                    showModal({
                        type: MODAL_PACK_CONFLICT,
                        pack,
                        conflicts: equipmentConflicts,
                        selectedConflicts: selectedExtras,
                        isConflictBetweenPackExtras: true,
                    }),
                );
            }
        }

        // Check for conflicts between packs
        const packConflicts = CarDataConsolidator.packHasConflictsBetweenPacks(
            pack,
            items.packs,
            basket.packs.concat(selectedPacks || []),
        );
        if (packConflicts) {
            return reject(
                showModal({
                    type: MODAL_PACK_CONFLICT,
                    pack,
                    conflicts: packConflicts,
                    selectedConflicts: selectedPacks || [],
                    isConflictBetweenPackExtras: false,
                }),
            );
        }

        try {
            allow({ ...action, selectedExtras, combinedSelectedExtras });
        } catch (exception) {
            return (reject as () => void)();
        }
    },

    process({ getState, action }, dispatch, done) {
        const { basket, items, commonSettings, bnbSettings } = getState();
        const selectedItem = action.type === ADD_PACK ? action.pack.id : action.extra.id;
        const actionString =
            action.type === ADD_PACK_EXTRA ? ValidateConfigAction.AddPackExtra : ValidateConfigAction.AddPack;
        const validatedConfig = getValidatedConfig(basket, actionString, items, selectedItem);

        // True when an extra was removed from the basket and not added as packExtra
        const extraRemoved = basket.extras.some(
            (extra) => !find(extra.id, validatedConfig.extras) && !find(extra.id, validatedConfig.packExtras),
        );

        const [extrasRemovedNotificationTitle, extrasRemovedNotificationDescription] = getBnbLabel(
            { commonSettings, bnbSettings },
            ["extrasRemovedNotificationTitle", "extrasRemovedNotificationDescription"],
        );

        if (extraRemoved) {
            dispatch(
                addNotification([
                    {
                        title: extrasRemovedNotificationTitle,
                        description: extrasRemovedNotificationDescription,
                    },
                ]),
            );
        }

        dispatch(setValidatedConfiguration(validatedConfig));
        // Don't dispatch the parent action if it's considering the same pack which was just added
        const rootAction = getRootAction(action);
        if (action.parentAction && !action._snapshot) {
            dispatch({ ...rootAction, parentAction: action } as unknown as AddPackType | AddPackExtraType);
        }
        done();
    },
});

const removePackSelectionLogic = (createLogic as BuildAndBuyLogicType<any>)({
    type: [REMOVE_PACK, REMOVE_PACK_EXTRA],

    validate({ action }, allow, reject) {
        // Do not allow deselection when the pack extra is standard
        // (in the case 2 standard pack extras exclude eachother they show as optional since you need to make a selection)
        if (action.extra && action.extra.standard) return (reject as () => void)();

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        const { basket, items, commonSettings, bnbSettings } = getState();
        const selectedItem = action.type === REMOVE_PACK ? action.pack.id : action.extra.id;
        const actionString =
            action.type === REMOVE_PACK_EXTRA ? ValidateConfigAction.RemovePackExtra : ValidateConfigAction.RemovePack;
        const validatedConfig = getValidatedConfig(basket, actionString, items, selectedItem);

        const [optionalPackExtrasRemovedNotificationTitle, optionalPackExtrasRemovedNotificationDescription] =
            getBnbLabel({ commonSettings, bnbSettings }, [
                "optionalPackExtrasRemovedNotificationTitle",
                "optionalPackExtrasRemovedNotificationDescription",
            ]);
        // Show a notification when a selected optional extra from a pack (packExtra) was removed because of deselecting a pack
        if (validatedConfig.packExtras.length !== basket.packExtras.length) {
            dispatch(
                addNotification([
                    {
                        title: optionalPackExtrasRemovedNotificationTitle,
                        description: optionalPackExtrasRemovedNotificationDescription,
                    },
                ]),
            );
        }
        dispatch(setValidatedConfiguration(validatedConfig));
        done();
    },
});

const resolvePackConflict = (createLogic as BuildAndBuyLogicType<ResolvePackConflictType>)({
    type: RESOLVE_CONFLICT_PACK,

    process({ action }, dispatch, done) {
        const { pack, selectedItem, selectedConflicts, conflicts, isConflictBetweenPackExtras } = action;

        // Filter conflict ids from selectedConflicts.
        const resolvedItems = (selectedConflicts as PackType[]).filter(
            (item) => !find(item.id, conflicts as PackType[]),
        );
        // Add selected item (pack/extra) which resolved the conflict.
        resolvedItems.push(selectedItem as PackType);

        // Get removed extras and remove them from basket only relevant when resolving conflicts between pack extras
        if (isConflictBetweenPackExtras) {
            const removedExtras = (selectedConflicts as ExtraType[]).filter(
                (extra) => !!find(extra.id, conflicts as ExtraType[]),
            );
            removedExtras.forEach((removedExtra) => dispatch(removePackExtra(pack, removedExtra, true)));
        } else {
            // In case of resolving a pack conflict we also need to add the current pack (which is being selected and caused the conflicts in the first place) to the resolvedItems array
            // Doing so will make sure that no double conflicts are triggered (if the "selectedItem" also spawns a conflict dialogue due to conflicting include rules towards "pack").
            resolvedItems.push(pack);
            dispatch(addPack(selectedItem as PackType, [], resolvedItems, true, false, false, action.transaction));
        }

        // There have been no scenarios which combine conflicting pack extras and conflicting pack includes so once we encounter such a scenario this might require fine tuning
        dispatch(
            addPack(
                pack,
                isConflictBetweenPackExtras ? (resolvedItems as unknown as ExtraType[]) : [],
                !isConflictBetweenPackExtras ? resolvedItems : [],
                true,
                false,
                false,
                action.transaction,
            ),
        );

        done();
    },
});

// Service plan logic
const addServiceProductLogic = (createLogic as BuildAndBuyLogicType<AddServiceProductType>)({
    type: ADD_SERVICE_PRODUCT,
    validate({ getState, action }, allow, reject) {
        const { basket, items } = getState();
        const { userSelected } = action;

        if (find(action.serviceProduct.id, basket.serviceProducts)) {
            return (reject as () => void)();
        }

        const serviceProductConflicts = CarDataConsolidator.getServiceProductConflicts(
            action.serviceProduct,
            items.serviceProducts,
            basket.serviceProducts,
            userSelected,
        );

        if (serviceProductConflicts.length > 0 && userSelected) {
            // If there are conflicts. Open conflict modal
            return reject(
                showModal({
                    type: MODAL_SERVICE_PRODUCT_CONFLICT,
                    serviceProduct: action.serviceProduct,
                    conflicts: serviceProductConflicts,
                }),
            );
        }

        allow(action);
    },
    process({ getState, action }, dispatch, done) {
        const { serviceProduct } = action;
        const { items } = getState();

        const excludedServiceProducts = CarDataConsolidator.getExcludedServiceProducts(
            serviceProduct,
            items.serviceProducts,
        );

        const includedServiceProducts = CarDataConsolidator.getIncludedServiceProducts(
            serviceProduct,
            items.serviceProducts,
        );

        if (includedServiceProducts.length > 0) {
            includedServiceProducts.forEach((includedServiceProduct) => {
                dispatch(addServiceProduct(includedServiceProduct, true, false));
            });
        }

        if (excludedServiceProducts.length > 0) {
            excludedServiceProducts.forEach((excludedServiceProduct) => {
                dispatch(removeServiceProduct(excludedServiceProduct, true));
            });
        }

        done();
    },
});

const resolveServiceProductLogic = (createLogic as BuildAndBuyLogicType<ResolveServiceProductConflictType>)({
    type: RESOLVE_SERVICE_PRODUCT_CONFLICT,

    process({ action }, dispatch, done) {
        const { serviceProduct, selectedItem } = action;

        dispatch(addServiceProduct(selectedItem, true, false));

        dispatch(addServiceProduct(serviceProduct, true, false));

        done();
    },
});

const removeServiceProductLogic = (createLogic as BuildAndBuyLogicType<RemoveServiceProductType>)({
    type: REMOVE_SERVICE_PRODUCT,
    process({ getState, action }, dispatch, done) {
        const { items } = getState();
        const { serviceProduct } = action;

        const includedServiceProducts = CarDataConsolidator.getIncludedServiceProducts(
            serviceProduct,
            items.serviceProducts,
        );

        // Remove the included service Products as they are a part of this serviceProduct
        if (includedServiceProducts.length > 0) {
            includedServiceProducts.forEach((includedServiceProduct) => {
                dispatch(removeServiceProduct(includedServiceProduct, true));
            });
        }

        done();
    },
});

const setWheelLogic = (createLogic as BuildAndBuyLogicType<SetWheelType>)({
    type: SET_WHEEL,
    validate({ getState, action }, allow, reject) {
        // eslint-disable-line consistent-return
        const { basket, items } = getState();
        const { extras } = basket;

        if (basket.wheel.id === action.wheel.id) return (reject as () => void)();

        const conflicts = CarDataConsolidator.extraHasConflicts(
            action.wheel as unknown as ExtraType,
            items.extras,
            extras,
        );
        if (conflicts)
            return reject(
                showModal({
                    type: MODAL_EXTRA_CONFLICT,
                    extra: action.wheel as unknown as ExtraType,
                    conflicts,
                    extraPendingStack: action.extraPendingStack || [],
                }),
            );

        action.previousWheel = basket.wheel;
        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        // Resolve the include/exclude rules from the current wheel.
        resolveAddExtraIncludExcludeRules(
            getState,
            action.wheel as unknown as ExtraType,
            dispatch,
            action.transaction,
            action.extraPendingStack,
        );
        done();
    },
});

export default [
    ...firstUserSelectedLogic, // Keep this logic always first!
    ...externalPriceLogic,
    calculatePriceLogic,
    setColourLogic,
    setPrimaryAccentColourLogic,
    setSecondaryAccentColourLogic,
    setUpholsteryLogic,
    addPackSelectionLogic,
    removePackSelectionLogic,
    resolvePackConflict,
    addExtraLogic,
    removeExtraLogic,
    addServiceProductLogic,
    removeServiceProductLogic,
    resolveServiceProductLogic,
    resolveExtraConflict,
    setExtraOptionLogic,
    setWheelLogic,
    ...lastUserSelectedLogic, // Keep this logic always last!
    ...HistoryLogic,
];

export const basketServerLogic = [calculatePriceLogic];
